// src/shared/hooks/useMeta.js
import { useEffect } from "react";
import { ImagePathNode } from "../constants";

export function useMeta(meta = {}) {
  useEffect(() => {
    const {
      title,
      description,
      keywords,
      favicon,
      canonical,
      geo,
      og,
      twitter,
      apple_touch_icon,
      alternate_hreflang,
      google_tags,
    } = meta;

    // ——— <title> ———
    if (title) document.title = title;

    // ——— Basic Meta ———
    if (description) updateMeta("description", description);
    if (keywords) updateMeta("keywords", keywords);

    // ——— Canonical Link ———
    if (canonical) updateLink("canonical", canonical);

    // ——— Favicon ———
    if (favicon) updateLink("icon", resolveUrl(favicon));

    // ——— Apple Touch Icon (Home only) ———
    if (apple_touch_icon) {
      updateLink("apple-touch-icon", resolveUrl(apple_touch_icon));
    }

    // ——— Alternate hreflang (Home only) ———
    if (alternate_hreflang?.lang && alternate_hreflang?.href) {
      updateAlternateLink(alternate_hreflang.lang, alternate_hreflang.href);
    }

    // ——— GEO TAGS ———
    if (geo?.placename) updateMeta("geo.placename", geo.placename);
    if (geo?.position) updateMeta("geo.position", geo.position);
    if (geo?.region) updateMeta("geo.region", geo.region);

    // ——— OPEN GRAPH TAGS ———
    if (og) {
      if (og.url) updateMeta("og:url", og.url, "property");
      if (og.type) updateMeta("og:type", og.type, "property");
      if (og.title) updateMeta("og:title", og.title, "property");
      if (og.site_name) updateMeta("og:site_name", og.site_name, "property");
      if (og.description)
        updateMeta("og:description", og.description, "property");
      if (og.image)
        updateMeta("og:image", resolveUrl(og.image), "property");
    }

    // ——— TWITTER TAGS ———
    if (twitter) {
      if (twitter.card) updateMeta("twitter:card", twitter.card, "name");
      if (twitter.domain) updateMeta("twitter:domain", twitter.domain, "name");
      if (twitter.url) updateMeta("twitter:url", twitter.url, "name");
      if (twitter.title) updateMeta("twitter:title", twitter.title, "name");
      if (twitter.description)
        updateMeta("twitter:description", twitter.description, "name");
      if (twitter.image)
        updateMeta("twitter:image", resolveUrl(twitter.image), "name");
    }

    // ——— GOOGLE TAGS (Home only) ———
    if (google_tags) {
      // Remove any old injected scripts first
      document
        .querySelectorAll("script[data-source='dynamic-google-tag']")
        .forEach((s) => s.remove());

      // Inject GTM (Google Tag Manager)
      if (google_tags.gtm_id) {
        const gtmScript = document.createElement("script");
        gtmScript.async = true;
        gtmScript.dataset.source = "dynamic-google-tag";
        gtmScript.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${google_tags.gtm_id}');
        `;
        document.head.appendChild(gtmScript);
      }

      // Inject Google Analytics (gtag)
      if (google_tags.gtag_id) {
        const gtagLoad = document.createElement("script");
        gtagLoad.async = true;
        gtagLoad.src = `https://www.googletagmanager.com/gtag/js?id=${google_tags.gtag_id}`;
        gtagLoad.dataset.source = "dynamic-google-tag";
        document.head.appendChild(gtagLoad);

        const gtagConfig = document.createElement("script");
        gtagConfig.dataset.source = "dynamic-google-tag";
        gtagConfig.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${google_tags.gtag_id}');
        `;
        document.head.appendChild(gtagConfig);
      }

      console.log("google_tags", google_tags);
      console.log("scripts", google_tags?.scripts);
      if (Array.isArray(google_tags.scripts)) {
        google_tags.scripts.forEach((code) => {
          if (!code) return;
          try {
            const customScript = document.createElement("script");
            customScript.dataset.source = "dynamic-google-tag";
            customScript.async = true;
            customScript.innerHTML = code;
            document.head.appendChild(customScript);
          } catch (error) {
            console.error("Error executing dynamic google tag script:", error);
          }
        });
      }
    }

    // ——— Helpers ———
    function updateMeta(name, content, attr = "name") {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    }

    function updateLink(rel, href) {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    }

    function updateAlternateLink(lang, href) {
      let el = document.head.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", lang);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    }

    function resolveUrl(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;
      if (path.startsWith("/uploads")) return `${ImagePathNode}${path}`;
      return `${ImagePathNode}${path}`;
    }

  }, [meta]);
}
